import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Alert,
} from '@mui/material';
import ImageCountDropZone from './ImageCountDropZone';
import useImageCount from './hooks/useImageCount';
import { usePdfThumbnails, useThumbnailLoader, PdfPageDialog } from '../PdfThumbnailRenderer';

// Generic page thumbnail component
function PageThumbnail({ pageNum, generateThumbnail, onPageClick }) {
  const { elementRef, thumbnail, isLoading } = useThumbnailLoader(pageNum, generateThumbnail);

  return (
    <Grid item xs={6} sm={4} md={3} lg={2} key={pageNum}>
      <Card
        ref={elementRef}
        sx={{
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: 6,
          },
        }}
        onClick={() => thumbnail && onPageClick(pageNum)}
      >
        {thumbnail ? (
          <Box
            component="img"
            src={thumbnail}
            alt={`Page ${pageNum}`}
            sx={{
              width: '100%',
              height: 150,
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 150,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#f5f5f5',
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Page {pageNum}
              </Typography>
            )}
          </Box>
        )}
        <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="body2">Page {pageNum}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

function ImageCountForm() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [openPageNumber, setOpenPageNumber] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const { countImages, isCountingImages, progress } = useImageCount(apiBaseUrl);

  const coloredPages = result?.colored_images_pages || [];
  const bwPages = result?.bw_images_pages || [];

  const { fileUrl, generateThumbnail, pdfDoc } = usePdfThumbnails(selectedFile);

  const handleFileSelect = useCallback(async (file) => {
    setSelectedFile(file);
    setResult(null);
    setError(null);

    try {
      const data = await countImages(file);
      setResult(data);
    } catch (err) {
      console.error(err);
      setError(`Failed to count images: ${err.message}`);
    }
  }, [countImages]);

  const handlePageClick = useCallback((pageNum) => {
    setOpenPageNumber(pageNum);
  }, []);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2} sx={{ p: 3, width: '100%', maxWidth: 1400 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 600 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ width: '100%', maxWidth: 600 }}>
        <ImageCountDropZone
          onFileSelect={handleFileSelect}
          selectedFile={selectedFile}
          isLoading={isCountingImages}
          disabled={false}
        />
        {isCountingImages && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant={progress?.phase === 'uploading' || progress?.phase === 'processing' ? 'determinate' : 'indeterminate'}
              value={
                progress?.phase === 'uploading' ? progress.pct :
                progress?.phase === 'processing' ? Math.round((progress.pagesDone / progress.totalPages) * 100) :
                undefined
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {progress?.phase === 'uploading' ? `Uploading… ${progress.pct}%` :
               progress?.phase === 'opening'   ? 'Opening PDF…' :
               progress?.phase === 'processing' ? `Analysing page ${progress.pagesDone} of ${progress.totalPages}…` :
               'Preparing…'}
            </Typography>
          </Box>
        )}
      </Box>

      {result && (
        <Paper elevation={3} sx={{ p: 3, width: '100%' }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Results
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Paper elevation={1} sx={{ p: 2, flex: 1, minWidth: 200, bgcolor: '#e3f2fd' }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {result.total_number_bw || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Black & White Images
              </Typography>
            </Paper>

            <Paper elevation={1} sx={{ p: 2, flex: 1, minWidth: 200, bgcolor: '#f3e5f5' }}>
              <Typography variant="h4" color="secondary" fontWeight="bold">
                {result.total_number_color || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Color Images
              </Typography>
            </Paper>

            <Paper elevation={1} sx={{ p: 2, flex: 1, minWidth: 200, bgcolor: '#fff3e0' }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {coloredPages.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pages with Color
              </Typography>
            </Paper>
          </Box>

          {/* BLACK & WHITE PAGES PREVIEW */}
          {bwPages.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Black & White Image Pages Preview
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Pages with B&W Images:</strong>{' '}
                {bwPages.length <= 20
                  ? bwPages.join(', ')
                  : `${bwPages.slice(0, 20).join(', ')}... and ${
                      bwPages.length - 20
                    } more`}
              </Typography>

              <Box
                sx={{
                  maxHeight: 600,
                  overflow: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: '#fafafa',
                }}
              >
                <Grid container spacing={2}>
                  {bwPages.map((pageNum) => (
                    <PageThumbnail
                      key={pageNum}
                      pageNum={pageNum}
                      generateThumbnail={generateThumbnail}
                      onPageClick={handlePageClick}
                    />
                  ))}
                </Grid>
              </Box>
            </Box>
          )}

          {/* COLORED PAGES PREVIEW */}
          {coloredPages.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Colored Image Pages Preview
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Pages with Colored Images:</strong>{' '}
                {coloredPages.length <= 20
                  ? coloredPages.join(', ')
                  : `${coloredPages.slice(0, 20).join(', ')}... and ${
                      coloredPages.length - 20
                    } more`}
              </Typography>

              <Box
                sx={{
                  maxHeight: 600,
                  overflow: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: '#fafafa',
                }}
              >
                <Grid container spacing={2}>
                  {coloredPages.map((pageNum) => (
                    <PageThumbnail
                      key={pageNum}
                      pageNum={pageNum}
                      generateThumbnail={generateThumbnail}
                      onPageClick={handlePageClick}
                    />
                  ))}
                </Grid>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {openPageNumber !== null && (
        <PdfPageDialog
          open={true}
          onClose={() => setOpenPageNumber(null)}
          fileUrl={fileUrl}
          pdfDoc={pdfDoc}
          pageNumber={openPageNumber}
        />
      )}
    </Box>
  );
}

export default ImageCountForm;
